<template>
  <div v-if="page" class="min-h-screen bg-gradient-to-b from-soft-white to-lavender-tint/20">
    <article class="container mx-auto px-6 pt-10 pb-16 max-w-6xl">
      <nav class="text-sm text-dark-gray/60 mb-4 flex flex-wrap gap-2">
        <RouterLink to="/" class="hover:text-light-purple">Home</RouterLink>
        <span>/</span>
        <RouterLink to="/services" class="hover:text-light-purple">Services</RouterLink>
        <span>/</span>
        <span class="text-dark-gray">{{ page.h1 }}</span>
      </nav>

      <div class="grid lg:grid-cols-[1.25fr_0.75fr] gap-10 items-start">
        <div>
          <p class="text-sm font-semibold text-light-purple uppercase tracking-wide mb-2">
            For {{ page.audience }}
          </p>
          <h1 class="text-3xl md:text-5xl font-poppins font-bold text-dark-gray mb-4 leading-tight">
            {{ page.h1 }}
          </h1>
          <p class="text-sm text-dark-gray/50 mb-6">Primary topic: {{ page.keyword }}</p>

          <div class="space-y-4 text-dark-gray/80 leading-relaxed mb-8">
            <p v-for="(p, i) in page.intro" :key="i" class="text-lg">{{ p }}</p>
          </div>

          <section class="card-neumorphic !p-6 mb-6">
            <h2 class="text-2xl font-semibold text-dark-gray mb-4">Why organisations choose Homvisit</h2>
            <ul class="space-y-3">
              <li v-for="b in page.benefits" :key="b" class="flex gap-2 text-dark-gray/80">
                <span class="text-light-purple font-bold">✓</span>
                <span>{{ b }}</span>
              </li>
            </ul>
          </section>

          <section class="card-neumorphic !p-6 mb-6">
            <h2 class="text-2xl font-semibold text-dark-gray mb-4">How it works</h2>
            <ol class="space-y-4">
              <li v-for="(step, i) in page.process" :key="step.title" class="flex gap-4">
                <span
                  class="w-8 h-8 rounded-full bg-light-purple text-white flex items-center justify-center shrink-0 text-sm font-bold"
                >{{ i + 1 }}</span>
                <div>
                  <h3 class="font-semibold text-dark-gray">{{ step.title }}</h3>
                  <p class="text-sm text-dark-gray/70">{{ step.text }}</p>
                </div>
              </li>
            </ol>
          </section>

          <section v-if="page.faqs?.length" class="card-neumorphic !p-6 mb-6">
            <h2 class="text-2xl font-semibold text-dark-gray mb-4">FAQs</h2>
            <div v-for="f in page.faqs" :key="f.q" class="mb-4 last:mb-0">
              <h3 class="font-semibold text-dark-gray mb-1">{{ f.q }}</h3>
              <p class="text-dark-gray/70 text-sm leading-relaxed">{{ f.a }}</p>
            </div>
          </section>

          <section class="card-neumorphic !p-6 mb-6">
            <h2 class="text-xl font-semibold text-dark-gray mb-3">Related</h2>
            <div class="flex flex-wrap gap-2">
              <RouterLink
                v-for="href in page.related"
                :key="href"
                :to="href"
                class="text-sm px-3 py-2 rounded-xl bg-lavender-tint/40 text-light-purple hover:bg-lavender-tint"
              >
                {{ href }}
              </RouterLink>
            </div>
          </section>

          <p class="text-sm text-dark-gray/60">
            Looking for a district page?
            <RouterLink to="/services/kochi" class="text-light-purple font-medium">Kochi</RouterLink>,
            <RouterLink to="/services/trivandrum" class="text-light-purple font-medium">Trivandrum</RouterLink>,
            <RouterLink to="/services/thiruvananthapuram" class="text-light-purple font-medium">Thiruvananthapuram</RouterLink>,
            or
            <RouterLink to="/services" class="text-light-purple font-medium">all services & districts</RouterLink>.
          </p>
        </div>

        <RichCtaCard
          :subject="`Enquiry — ${page.keyword}`"
          :source-page="`/services/${page.slug}`"
          title="Get a corporate / partner quote"
          subtitle="Tell us your district, headcount, and timeline."
        />
      </div>
    </article>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import RichCtaCard from '../components/RichCtaCard.vue'
import { getCommercialService } from '../config/commercialServices'
import { SITE_URL } from '../config/contact'
import {
  breadcrumbSchema,
  faqSchema,
  graphSchema,
  organizationSchema,
  serviceSchema,
  useSeo,
} from '../composables/useSeo'

export default {
  name: 'CommercialServicePage',
  components: { RichCtaCard },
  setup() {
    const route = useRoute()
    const page = computed(() => getCommercialService(route.params.slug))

    useSeo(
      computed(() => {
        const p = page.value
        if (!p) return { title: 'Not found', robots: 'noindex', path: '/services' }
        const path = `/services/${p.slug}`
        return {
          title: p.title,
          description: p.description,
          path,
          keywords: [p.keyword, 'Homvisit Kerala', 'phlebotomy partner'],
          schema: graphSchema([
            organizationSchema(),
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Services', path: '/services' },
              { name: p.h1, path },
            ]),
            serviceSchema({
              name: p.h1,
              description: p.description,
              area: 'Kerala',
              url: `${SITE_URL}${path}`,
            }),
            faqSchema(p.faqs),
          ]),
        }
      })
    )

    return { page }
  },
}
</script>
